import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <article className="group cursor-pointer">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="relative overflow-hidden rounded-lg mb-4">
            <div className="w-full h-56">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
          </div>
          <h3 className="font-heading text-lg font-bold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-6 h-6 rounded-full overflow-hidden">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xs text-foreground-500">{article.author}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-500">{article.publishedAt}</span>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group">
        <Link to={`/article/${article.slug}`} className="flex gap-4">
          <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {article.category}
            </span>
            <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors mt-1.5 line-clamp-2">
              {article.title}
            </h3>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs text-foreground-500">{article.readTime} 分钟</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-500">{article.publishedAt}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300">
      <Link to={`/article/${article.slug}`} className="block">
        <div className="w-full h-48 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime} 分钟</span>
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-5 h-5 rounded-full overflow-hidden">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xs text-foreground-500">{article.author}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}